import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Layout
import Layout from '../components/layout/Layout';

// Auth pages
import Login from '../auth/Login';
// import Register from '../auth/Register';
import ForgotPassword from '../auth/ForgotPassword';
import ResetPassword from '../auth/ResetPassword';

// Main pages
import Dashboard from '../pages/dashboard/Dashboard';
import Profile from '../pages/profile/Profile';
import Settings from '../pages/settings/Settings';

// Document pages
import DocumentList from '../pages/documents/DocumentList';
import DocumentCreate from '../pages/documents/DocumentCreate';
import DocumentEdit from '../pages/documents/DocumentEdit';
import DocumentView from '../pages/documents/DocumentView';
// import DocumentWorkflow from '../pages/documents/DocumentWorkflow';
// import DocumentMetrics from '../pages/documents/DocumentMetrics';
import AssignWorkflow from '../pages/documents/AssignWorkflow';
import WorkflowStepAdd from '../pages/documents/WorkflowStepAdd';

import Report from '../pages/reports/Report';

// Workflow pages
import TaskIndex from '../pages/Task/TaskAssignedTome';
import TaskAssignedByme from '../pages/Task/TaskAssignedByme';

// User pages
import UserList from '../pages/users/UserList';
import UserCreate from '../pages/users/UserCreate';
import UserEdit from '../pages/users/UserEdit';
import UserView from '../pages/users/UserView';

// Role pages
import RoleList from '../pages/roles/RoleList';
import RoleCreate from '../pages/roles/RoleCreate';
import RoleEdit from '../pages/roles/RoleEdit';

// Department pages
import DepartmentList from '../pages/departments/DepartmentList';
import DepartmentCreate from '../pages/departments/DepartmentCreate';
import DepartmentEdit from '../pages/departments/DepartmentEdit';

// Other pages
import NotFound from '../pages/errors/NotFound';
import AccessDenied from '../pages/errors/AccessDenied';
import Notifications from '../pages/notifications/Notifications';

// Super Admin pages
import SuperAdminDashboard from '../pages/superadmin/SuperAdminDashboard';
import DocumentFlowViewer from '../pages/superadmin/DocumentFlowViewer';

// Limited Admin pages
import LimitedAdminDashboard from '../pages/LimitedAdminDashboard';
import LimitedAdminDocumentFlowViewer from '../pages/limitedadmin/LimitedAdminDocumentFlowViewer';

// Ministry pages
import MinistryList from '../pages/ministries/MinistryList';
import MinistryCreate from '../pages/ministries/MinistryCreate';
import MinistryEdit from '../pages/ministries/MinistryEdit';
import MinistryView from '../pages/ministries/MinistryView';

// Province pages
import ProvinceList from '../pages/provinces/ProvinceList';
import ProvinceCreate from '../pages/provinces/ProvinceCreate';
import ProvinceEdit from '../pages/provinces/ProvinceEdit';
import ProvinceView from '../pages/provinces/ProvinceView';

// Provincial Directorate pages
import DirectorateList from '../pages/directorates/DirectorateList';
import DirectorateCreate from '../pages/directorates/DirectorateCreate';
import DirectorateEdit from '../pages/directorates/DirectorateEdit';
import DirectorateView from '../pages/directorates/DirectorateView';

// Import DeputyMinistry pages
import DeputyMinistryList from '../pages/deputy-ministries/DeputyMinistryList';
import DeputyMinistryCreate from '../pages/deputy-ministries/DeputyMinistryCreate';
import DeputyMinistryEdit from '../pages/deputy-ministries/DeputyMinistryEdit';

// Protected route component
const ProtectedRoute = ({ element }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Show loading or redirect to login if not authenticated
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return isAuthenticated ? element : <Navigate to="/login" />;
};

// Super Admin route component
const SuperAdminRoute = ({ element }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  // Show loading or redirect to login if not authenticated
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user has super admin access
  const isSuperAdmin = (
    (user?.role?.name && user.role.name.toLowerCase().includes('super')) ||
    (Array.isArray(user?.roles) && user.roles.some(r => r.toLowerCase().includes('super'))) ||
    (user?.role?.permissions && user.role.permissions.some(p => p.code === 'super_admin_access')) ||
    (user?.permissions && user.permissions.includes('super_admin_access')) ||
    (user?.email === 'admin@gmail.com') // Fallback for testing
  );
  
  return isSuperAdmin ? element : <Navigate to="/access-denied" />;
};

// Limited Admin route component
const LimitedAdminRoute = ({ element }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  // Show loading or redirect to login if not authenticated
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user has limited admin access
  const isLimitedAdmin = (
    (user?.role?.name && user.role.name === 'Limited Admin') ||
    (user?.role?.Permissions && user.role.Permissions.some(p => p.code === 'limited_admin_access'))
  );
  
  return isLimitedAdmin ? element : <Navigate to="/access-denied" />;
};

// Public route component (accessible only when not logged in)
const PublicRoute = ({ element }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Show loading or redirect to dashboard if already authenticated
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" /> : element;
};

const AppRouter = () => {
  return (
    <Routes>
      {/* Public routes (only accessible when not logged in) */}
      <Route path="/login" element={<PublicRoute element={<Login />} />} />
      {/* <Route path="/register" element={<PublicRoute element={<Register />} />} /> */}
      <Route path="/forgot-password" element={<PublicRoute element={<ForgotPassword />} />} />
      <Route path="/reset-password/:token" element={<PublicRoute element={<ResetPassword />} />} />
      
      {/* Protected routes (requires authentication) */}
      <Route path="/" element={<ProtectedRoute element={<Layout />} />}>
        {/* Dashboard */}
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Super Admin Dashboard */}
        <Route path="super-admin" element={<SuperAdminRoute element={<SuperAdminDashboard />} />} />
        <Route path="super-admin/document-flow/:documentId" element={<SuperAdminRoute element={<DocumentFlowViewer />} />} />
        
        {/* Limited Admin Dashboard */}
        <Route path="limited-admin" element={<LimitedAdminRoute element={<LimitedAdminDashboard />} />} />
        <Route path="limited-admin/document-flow/:documentId" element={<LimitedAdminRoute element={<LimitedAdminDocumentFlowViewer />} />} />
        
        {/* Documents */}
        <Route path="documents" element={<DocumentList />} />
        <Route path="documents/create" element={<DocumentCreate />} />
        <Route path="documents/:id/edit" element={<DocumentEdit />} />
        <Route path="documents/:id" element={<DocumentView />} />
        {/* <Route path="documents/:documentId/workflow" element={<DocumentWorkflow />} /> */}
        {/* <Route path="documents/:documentId/metrics" element={<DocumentMetrics />} /> */}
        <Route path="documents/:documentId/assign-workflow" element={<AssignWorkflow />} />



        {/* Tasks */}
        <Route path="tasks" element={<TaskIndex />} />
        <Route path="tasks/assigned-by-me" element={<TaskAssignedByme />} />
        
  
        
        {/* Users */}
        <Route path="users" element={<UserList />} />
        <Route path="users/create" element={<UserCreate />} />
        <Route path="users/:id/edit" element={<UserEdit />} />
        <Route path="users/:id" element={<UserView />} />
        
        {/* Roles */}
        <Route path="roles" element={<RoleList />} />
        <Route path="roles/create" element={<RoleCreate />} />
        <Route path="roles/:id/edit" element={<RoleEdit />} />
        
        {/* Departments */}
        <Route path="departments" element={<DepartmentList />} />
        <Route path="departments/create" element={<DepartmentCreate />} />
        <Route path="departments/:id/edit" element={<DepartmentEdit />} />
        
        <Route path="reports" element={<Report />} />

        {/* Ministries */}
        <Route path="deputy-ministries" element={<DeputyMinistryList />} />
        <Route path="deputy-ministries/create" element={<DeputyMinistryCreate />} />
        <Route path="deputy-ministries/:id/edit" element={<DeputyMinistryEdit />} />
        
        <Route path="ministries" element={<MinistryList />} />
        <Route path="ministries/create" element={<MinistryCreate />} />
        <Route path="ministries/:id/edit" element={<MinistryEdit />} />
        
        {/* Provinces */}
        <Route path="provinces" element={<ProvinceList />} />
        <Route path="provinces/create" element={<ProvinceCreate />} />
        <Route path="provinces/:id/edit" element={<ProvinceEdit />} />
        <Route path="provinces/:id" element={<ProvinceView />} />
        
        {/* Directorates */}
        <Route path="directorates" element={<DirectorateList />} />
        <Route path="directorates/create" element={<DirectorateCreate />} />
        <Route path="directorates/:id/edit" element={<DirectorateEdit />} />
        <Route path="directorates/:id" element={<DirectorateView />} />
        
        
        {/* User Profile & Settings */}
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="notifications" element={<Notifications />} />
        
        {/* Deputy Ministries */}
        <Route path="deputy-ministries" element={<DeputyMinistryList />} />
        <Route path="deputy-ministries/create" element={<DeputyMinistryCreate />} />
        <Route path="deputy-ministries/:id/edit" element={<DeputyMinistryEdit />} />
      </Route>
      
      {/* Error pages */}
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRouter; 